import { useId, useRef, useState } from 'react';
import type { StockSurplus } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { LedgerRow } from './CompositionLedger';
import { traceReason, traceStateLabel } from './compositionText';
import { SurplusLocationExplanation } from './SurplusLocationExplanation';
import { surplusQuantity } from './surplusTracePresentation';
export { surplusQuantity } from './surplusTracePresentation';

function settingsIdentityLabel(
  identity: StockSurplus['settingsIdentity'],
  gt: ReturnType<typeof useGT>,
): string {
  if (identity?.source === 'saved')
    return gt('Saved settings · revision {revision}', {
      context: 'surplus saved revision label',
      revision: identity.revision ?? '—',
    });
  if (identity?.source === 'draft')
    return gt('Unsaved draft', { context: 'surplus preview draft label' });
  return gt('Default (v1-equivalent), not saved', {
    context: 'surplus implicit settings label',
  });
}

/** Scope-wide limitations and source rules, after the numbers they qualify. */
function ExplanationFootnotes({
  result,
  selectedKinds,
}: {
  result: StockSurplus;
  selectedKinds: ReadonlySet<string>;
}) {
  const gt = useGT();
  const hasSales = selectedKinds.has('historical_customer_sales');
  const hasOrderLimitations =
    selectedKinds.has('existing_commitment_bundle') ||
    selectedKinds.has('open_sales_order_demand');
  const hasTransferLimitations =
    selectedKinds.has('existing_commitment_bundle') ||
    selectedKinds.has('staged_unacknowledged_transfers');
  return (
    <div className="space-y-1 border-t pt-2 text-muted-foreground">
      {result.locations.length > 1 && (
        <p className="text-foreground">
          <T context="surplus per location aggregation">
            Calculate and clamp each location separately, then sum. One
            location's shortfall does not offset another's surplus.
          </T>
        </p>
      )}
      {result.limitations.flatMap((limitation) =>
        limitation === 'cross_location_quantity_unit_unverified' ||
        limitation === 'aggregate_numeric_overflow'
          ? [<p key={limitation}>{traceReason(limitation, gt)}</p>]
          : [],
      )}
      {hasSales && (
        <p>
          {gt('Observation window: {start} to {end} (end excluded)', {
            context: 'surplus actual complete-month observation date window',
            start: result.windowStart,
            end: result.windowEndExclusive,
          })}
        </p>
      )}
      {hasOrderLimitations && (
        <p>
          <T context="selected surplus sales order source limitations">
            Existing sales order rules apply a 30-day cutoff and use ordered
            quantities for partial shipments.
          </T>
        </p>
      )}
      {hasTransferLimitations && (
        <p>
          <T context="selected surplus staged transfer source limitations">
            Commitment quantities may lag after a transfer is acknowledged.
          </T>
        </p>
      )}
      <p>
        <T context="surplus diagnostic boundary">
          Uses current stock and selected protection. Diagnostic only; this does
          not authorize transfers or change replenishment.
        </T>
      </p>
      <p>
        {gt('Evaluated at: {date} · Timezone: {timezone}', {
          context: 'surplus evaluation provenance',
          date: result.evaluatedAt,
          timezone: result.timezone,
        })}
      </p>
    </div>
  );
}

export function SurplusExplanation({
  result,
  locationNames = new Map(),
  headingId,
}: {
  result: StockSurplus;
  locationNames?: ReadonlyMap<string, string>;
  headingId?: string;
}) {
  const gt = useGT();
  const selectedKinds = new Set(
    result.locations.flatMap(
      (row) =>
        row.explanation?.components.flatMap((component) =>
          component.state !== 'not_selected' ? [component.kind] : [],
        ) ?? [],
    ),
  );
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 id={headingId} className="text-sm font-semibold">
          <T context="surplus explanation heading">How surplus is calculated</T>
        </h3>
        <span className="text-muted-foreground">
          {settingsIdentityLabel(result.settingsIdentity, gt)}
        </span>
      </div>
      {result.status === 'estimated' && (
        <p className="font-medium">
          <T context="surplus explanation estimated result">
            Estimated surplus · based on partial sales history
          </T>
        </p>
      )}
      <div className="border-y text-sm">
        <LedgerRow
          operator="="
          emphasis
          label={<T context="surplus complete scope result">Total surplus</T>}
          value={
            result.status === 'unavailable'
              ? traceStateLabel(result.status, gt)
              : surplusQuantity(result.quantity)
          }
        />
      </div>
      {result.locations.map((row, index) => (
        <SurplusLocationExplanation
          key={row.warehouseId}
          row={row}
          name={
            locationNames.get(row.warehouseId) ??
            gt('Location {index}', {
              context: 'surplus explanation unnamed readable location',
              index: index + 1,
            })
          }
        />
      ))}
      <ExplanationFootnotes result={result} selectedKinds={selectedKinds} />
    </div>
  );
}

export function SurplusExplanationTrigger({
  result,
  locationNames,
}: {
  result: StockSurplus;
  locationNames: ReadonlyMap<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const dismissedByEscape = useRef(false);
  const headingId = useId();
  const popupRef = useRef<HTMLDivElement | null>(null);
  const gt = useGT();
  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen, details) => {
        if (!nextOpen && details.reason === 'escape-key')
          dismissedByEscape.current = true;
        if (nextOpen && dismissedByEscape.current) {
          details.cancel();
          return;
        }
        setOpen(nextOpen);
      }}
    >
      <PopoverTrigger
        className="rounded-sm text-right tabular-nums underline decoration-muted-foreground/60 decoration-dotted underline-offset-4 hover:decoration-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        aria-label={gt('{status} {quantity}. How this surplus is calculated', {
          context: 'surplus quantity explanation accessible trigger',
          status:
            result.status === 'estimated'
              ? gt('Estimated surplus', {
                  context: 'surplus estimate trigger label',
                })
              : gt('Surplus', { context: 'surplus trigger label' }),
          quantity:
            result.status === 'unavailable'
              ? gt('unavailable', { context: 'surplus unavailable status' })
              : surplusQuantity(result.quantity),
        })}
        openOnHover
        delay={150}
        closeDelay={200}
        onMouseEnter={() => {
          dismissedByEscape.current = false;
        }}
        onPointerDown={() => {
          dismissedByEscape.current = false;
        }}
        onKeyDown={(event) => {
          if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ')
            dismissedByEscape.current = false;
        }}
        onBlur={(event) => {
          const destination = event.relatedTarget;
          if (
            destination instanceof Element &&
            !destination.closest(
              '[data-slot="popover-content"], [data-base-ui-focus-guard]',
            )
          )
            dismissedByEscape.current = false;
        }}
        onFocus={(event) => {
          if (
            !dismissedByEscape.current &&
            event.currentTarget.matches(':focus-visible')
          )
            setOpen(true);
        }}
      >
        {result.status === 'unavailable' ? (
          <T context="surplus unavailable value">Unavailable</T>
        ) : (
          surplusQuantity(result.quantity)
        )}
      </PopoverTrigger>
      <PopoverContent
        ref={popupRef}
        initialFocus={popupRef}
        aria-labelledby={headingId}
        className="max-h-[min(36rem,80vh)] w-[min(30rem,calc(100vw-2rem))] overflow-y-auto p-3 motion-reduce:data-[open]:animate-none motion-reduce:data-[ending-style]:animate-none"
        align="end"
      >
        <SurplusExplanation
          result={result}
          locationNames={locationNames}
          headingId={headingId}
        />
      </PopoverContent>
    </Popover>
  );
}
